/**
 * Platform Storage Service
 * Handles file upload/download to OptiLogic platform storage
 */

import { getUserAppKey } from './optilogicService';

const API_BASE_URL = 'https://api.optilogic.app/v0/Studio/file';

/**
 * Upload a file to platform storage
 * @param {string} directoryPath - Platform directory path
 * @param {string} filename - Name of the file
 * @param {Blob|string} fileContent - File content (Blob or string)
 * @returns {Promise<void>}
 */
export async function uploadFile(directoryPath, filename, fileContent) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const url = `${API_BASE_URL}/${encodeURIComponent(directoryPath)}/${encodeURIComponent(filename)}?overwrite=true`;

    // Convert string content to Blob if needed
    const blob = fileContent instanceof Blob
        ? fileContent
        : new Blob([fileContent], { type: 'application/octet-stream' });

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'X-APP-KEY': appKey,
            'Content-Type': 'application/octet-stream'
        },
        body: blob
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`File upload failed (${response.status}): ${errorText}`);
    }
}

/**
 * Download a file from platform storage
 * @param {string} directoryPath - Platform directory path
 * @param {string} filename - Name of the file
 * @returns {Promise<string>} File content as text
 */
export async function downloadFile(directoryPath, filename) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const url = `${API_BASE_URL}/${encodeURIComponent(directoryPath)}/${encodeURIComponent(filename)}?op=download`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'X-APP-KEY': appKey
        }
    });

    print(url)

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`File download failed (${response.status}): ${errorText}`);
    }

    return await response.text();
}

/**
 * Delete a file from platform storage
 * @param {string} directoryPath - Platform directory path
 * @param {string} filename - Name of the file
 * @returns {Promise<void>}
 */
export async function deleteFile(directoryPath, filename) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const url = `${API_BASE_URL}/${encodeURIComponent(directoryPath)}/${encodeURIComponent(filename)}?op=download`;

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'X-APP-KEY': appKey
        }
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`File deletion failed (${response.status}): ${errorText}`);
    }
}
